import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ExternalLink, Download, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import API from '../config/api';

// Session-scoped blob cache keyed by immutable pdf_file_id — makes
// repeat previews instant even when the ingress strips Cache-Control
// (observed on the hosted proxy: endpoint 'private, immutable' gets
// rewritten to 'no-store'). Each regenerate/resend mints a NEW
// pdf_file_id, so cached entries can never go stale.
const _pdfBlobCache = new Map();
const PDF_CACHE_MAX = 15;
const cachePdfBlob = (key, blob) => {
  if (!key) return;
  if (_pdfBlobCache.size >= PDF_CACHE_MAX) {
    const oldest = _pdfBlobCache.keys().next().value;
    _pdfBlobCache.delete(oldest);
  }
  _pdfBlobCache.set(key, blob);
};

/**
 * Jul 2026 — MLC-PREVIEW-PERMANENT (Part C).
 * In-app Mail-Away PDF viewer. Replaces the legacy blank-tab +
 * "Preparing Mail-Away PDF…" flow: the dialog opens instantly in the
 * same frame (no popup blockers, no tab handoff) and the PDF streams
 * into an iframe. Normal path is a pure stored read (<1s); the
 * "Rebuilding PDF…" state only appears on the exceptional self-healing
 * path when the stored PDF is missing and the backend regenerates it.
 * Immutable caching: when the row carries pdf_file_id we request
 * ?v=<id> so repeat previews resolve from the browser HTTP cache.
 */
export const MailAwayPdfViewerDialog = ({ open, onOpenChange, orderId, request }) => {
  const [phase, setPhase] = useState('loading'); // loading | rebuilding | ready | error
  const [blobUrl, setBlobUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [servedFrom, setServedFrom] = useState('');
  const [fetchNonce, setFetchNonce] = useState(0);
  const blobRef = useRef('');

  useEffect(() => {
    if (!open || !orderId || !request?.request_id) return undefined;
    let cancelled = false;
    setPhase('loading');
    setErrorMsg('');
    setServedFrom('');

    const cacheKey = request.pdf_file_id
      ? `${orderId}:${request.request_id}:${request.pdf_file_id}`
      : null;

    const showBlob = (blob, from) => {
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      blobRef.current = url;
      setBlobUrl(url);
      setServedFrom(from);
      setPhase('ready');
    };

    // Instant repeat previews: immutable pdf_file_id → session cache hit,
    // zero network round-trip.
    const cached = cacheKey && _pdfBlobCache.get(cacheKey);
    if (cached) {
      showBlob(cached, 'session_cache');
      return () => {
        cancelled = true;
        if (blobRef.current) {
          URL.revokeObjectURL(blobRef.current);
          blobRef.current = '';
        }
        setBlobUrl('');
      };
    }

    // Exceptional self-heal path takes seconds — after 2.5s switch the
    // copy from "Loading" to "Rebuilding" so the user knows what's up.
    const rebuildTimer = setTimeout(() => {
      setPhase((p) => (p === 'loading' ? 'rebuilding' : p));
    }, 2500);

    const token = localStorage.getItem('token') || '';
    const versionParam = request.pdf_file_id
      ? `?v=${encodeURIComponent(request.pdf_file_id)}`
      : '';

    fetch(
      `${API}/api/orders/${orderId}/mailaway/requests/${request.request_id}/pdf${versionParam}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(async (r) => {
        if (!r.ok) {
          let detail = '';
          try {
            detail = (await r.json())?.detail || '';
          } catch (e) { /* not JSON */ }
          throw new Error(detail || `HTTP ${r.status}`);
        }
        const from = r.headers.get('X-Mailaway-Served-From') || '';
        const servedId = r.headers.get('X-Mailaway-Pdf-File-Id') || '';
        const blob = await r.blob();
        // Cache under the id we actually got (covers self-heal replacing
        // a stale id) — immutable per backend contract.
        if (servedId) {
          cachePdfBlob(`${orderId}:${request.request_id}:${servedId}`, blob);
        }
        if (cacheKey && (!servedId || servedId === request.pdf_file_id)) {
          cachePdfBlob(cacheKey, blob);
        }
        showBlob(blob, from);
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err.message || 'Unknown error');
        setPhase('error');
      })
      .finally(() => clearTimeout(rebuildTimer));

    return () => {
      cancelled = true;
      clearTimeout(rebuildTimer);
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = '';
      }
      setBlobUrl('');
    };
  }, [open, orderId, request?.request_id, request?.pdf_file_id, fetchNonce]);

  const filename =
    request?.pdf_filename ||
    `${request?.jurisdiction_name || 'Mail-Away'} — MLC Letter.pdf`;

  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  const downloadPdf = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0"
        data-testid="mailaway-pdf-viewer-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
          <div className="min-w-0 pr-4">
            <DialogTitle className="text-sm font-semibold text-slate-800 truncate">
              Mail-Away PDF — {request?.jurisdiction_name || 'Preview'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              In-app preview of the stored Mail-Away MLC letter PDF
            </DialogDescription>
            <p className="text-xs text-slate-400 truncate mt-0.5" data-testid="mailaway-pdf-viewer-filename">
              {filename}
              {servedFrom === 'regenerated' && ' · rebuilt just now'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 mr-6">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={openInNewTab}
              disabled={phase !== 'ready'}
              data-testid="mailaway-pdf-viewer-open-new-tab"
            >
              <ExternalLink size={12} className="mr-1" /> Open in new tab
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={downloadPdf}
              disabled={phase !== 'ready'}
              data-testid="mailaway-pdf-viewer-download"
            >
              <Download size={12} className="mr-1" /> Download
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-100">
          {phase === 'ready' && blobUrl && (
            <iframe
              src={blobUrl}
              title={filename}
              className="w-full h-full border-0"
              data-testid="mailaway-pdf-viewer-iframe"
            />
          )}
          {(phase === 'loading' || phase === 'rebuilding') && (
            <div
              className="h-full flex flex-col items-center justify-center gap-3 text-slate-500"
              data-testid={phase === 'rebuilding' ? 'mailaway-pdf-viewer-rebuilding' : 'mailaway-pdf-viewer-loading'}
            >
              <Loader2 className="animate-spin" size={28} />
              {phase === 'rebuilding' ? (
                <>
                  <p className="text-sm font-medium text-slate-700">Rebuilding PDF…</p>
                  <p className="text-xs text-slate-400 max-w-sm text-center">
                    The stored document is being restored. The preview will open
                    automatically when ready.
                  </p>
                </>
              ) : (
                <p className="text-sm">Loading preview…</p>
              )}
            </div>
          )}
          {phase === 'error' && (
            <div
              className="h-full flex flex-col items-center justify-center gap-3 text-slate-500"
              data-testid="mailaway-pdf-viewer-error"
            >
              <AlertCircle size={28} className="text-red-500" />
              <p className="text-sm font-medium text-red-600">Preview not available</p>
              <p className="text-xs text-slate-500 max-w-sm text-center">{errorMsg}</p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs mt-1"
                onClick={() => setFetchNonce((n) => n + 1)}
                data-testid="mailaway-pdf-viewer-retry"
              >
                <RefreshCw size={12} className="mr-1" /> Retry
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MailAwayPdfViewerDialog;
